import { Checkbox, Divider, Modal, ModalBody, ModalContent, ModalHeader } from "@cronika/ui";
import { ShareSocialDownload, ShareSocialTelegram, ShareSocialX } from "@cronika/assets/icons";
import { useTranslation } from "@cronika/locales";
import { BetType, type BetMessage } from "@cronika/proto/common/trading_pb";
import { FC, useEffect, useState } from "react";
import { API_BASE_URL } from "@/shared/config/config";

type ShareMetric = "pnl" | "roi";

function formatUSD(value: number): string {
  return value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatPnlLabel(value: number): string {
  return `${value < 0 ? "-" : "+"}$${formatUSD(Math.abs(value))}`;
}

function formatRoiLabel(value: number): string {
  return `${value < 0 ? "-" : "+"}${Math.round(Math.abs(value))}%`;
}

function shareCardUrl(betId: string, metric: ShareMetric, lang: string): string {
  return `${API_BASE_URL}/rest/share/${betId}/card.png?metric=${metric}&lang=${lang}&v=2`;
}

function sharePageUrl(betId: string, metric: ShareMetric, lang: string): string {
  return `${API_BASE_URL}/rest/share/${betId}?metric=${metric}&lang=${lang}`;
}

interface ShareResultsModalProps {
  bet: BetMessage;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ShareResultsModal: FC<ShareResultsModalProps> = ({ bet, open, onOpenChange }) => {
  const { t, i18n } = useTranslation("trading");
  const [metric, setMetric] = useState<ShareMetric>("pnl");
  const [isDownloading, setIsDownloading] = useState(false);
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const lang = i18n.language;

  const amount = Number(bet.amount);
  const profitLoss = Number(bet.profitLoss ?? 0);
  const roiPercent = amount > 0 ? (profitLoss / amount) * 100 : 0;
  const direction = bet.betType === BetType.CALL ? "buy" : "sell";

  useEffect(() => {
    if (!open) return;
    setLoadedSrc(null);
    for (const m of ["pnl", "roi"] as const) {
      const img = new Image();
      img.src = shareCardUrl(bet.id, m, lang);
    }
  }, [open, bet.id, lang]);

  const currentSrc = shareCardUrl(bet.id, metric, lang);
  const isCurrentLoaded = loadedSrc === currentSrc;

  const shareText = t("history.share.post-text", {
    symbol: bet.symbol,
    headline: metric === "roi" ? formatRoiLabel(roiPercent) : formatPnlLabel(profitLoss),
  });

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const res = await fetch(shareCardUrl(bet.id, metric, lang));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cronika-trade-${bet.id}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleShareX = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(
      sharePageUrl(bet.id, metric, lang),
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleShareTelegram = () => {
    const url = `https://t.me/share/url?url=${encodeURIComponent(sharePageUrl(bet.id, metric, lang))}&text=${encodeURIComponent(
      shareText,
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent
        size="lg"
        backdrop="blur"
        overlayClassName="bg-black/30"
        className="z-[101] border backdrop-blur-xl border-white/10 shadow-2xl max-w-[calc(100%-32px)] sm:max-w-2xl"
        style={{ backgroundColor: "rgba(20, 20, 20, 0.4)" }}
      >
        <ModalHeader className="border-b border-white/10 font-semibold">{t("history.share.title")}</ModalHeader>
        <ModalBody className="p-5 pt-0 flex flex-col gap-4">
          <div className="relative border border-white/10 bg-black aspect-[1200/582] overflow-hidden">
            {!isCurrentLoaded && <div className="absolute inset-0 animate-pulse bg-white/5" />}
            <img
              key={currentSrc}
              src={currentSrc}
              alt={`${bet.symbol} ${direction} result`}
              onLoad={() => setLoadedSrc(currentSrc)}
              className={`w-full h-full object-cover transition-opacity duration-200 ${
                isCurrentLoaded ? "opacity-100" : "opacity-0"
              }`}
            />
          </div>

          <Divider />

          <div className="flex flex-col items-center gap-2">
            <div className="flex items-center gap-6">
              <Checkbox
                label={t("history.share.total-pl")}
                checked={metric === "pnl"}
                onCheckedChange={(checked) => checked && setMetric("pnl")}
                color="warning"
              />
              <Checkbox
                label={t("history.share.roi")}
                checked={metric === "roi"}
                onCheckedChange={(checked) => checked && setMetric("roi")}
                color="warning"
              />
            </div>
          </div>

          <div className="flex items-center justify-center gap-3 pt-2">
            <button
              type="button"
              aria-label={t("history.share.share-x")}
              onClick={handleShareX}
              className="rounded-full transition-opacity hover:opacity-80 cursor-pointer"
            >
              <ShareSocialX className="w-10 h-10" />
            </button>
            <button
              type="button"
              aria-label={t("history.share.share-telegram")}
              onClick={handleShareTelegram}
              className="rounded-full transition-opacity hover:opacity-80 cursor-pointer"
            >
              <ShareSocialTelegram className="w-10 h-10" />
            </button>
            <button
              type="button"
              aria-label={t("history.share.download")}
              disabled={isDownloading}
              onClick={handleDownload}
              className="rounded-full transition-opacity hover:opacity-80 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShareSocialDownload className="w-10 h-10" />
            </button>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
